//Question Slice
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { IAuthResponse } from '../auth/IAuthState';
import { createQuestion, deleteQuestion, fetchQuestions, updateQuestion } from './questionAsyncThunk';
import { toast } from 'react-toastify';
import { Question, SubCategory } from '../../../types';
export interface CreateQuestionRequest {
    category: string;
    name: string[];
}

// Request body for updating a question
export interface UpdateQuestionRequest {
    name: string;
    category: string;
}

export interface DeleteQuestion extends IAuthResponse {
    id: string;
}

interface questionState {
    data: Question[];
    selectedSubCategory: SubCategory | null;
    total: number; // Total records for pagination
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    loading: boolean;
    isModalOpen: boolean;
    isEdit: boolean;
    editingRecord: Question | null;
}

const initialState: questionState = {
    data: [],
    total: 0, // Initialize total records
    status: 'idle',
    selectedSubCategory: null,
    error: null,
    loading: false,
    isModalOpen: false,
    isEdit: false,
    editingRecord: null,
};

// Slice
const questionSlice = createSlice({
    name: 'question',
    initialState,
    reducers: {
        setModalOpen: (state, action) => {
            state.isModalOpen = action.payload;
        },
        setEdit: (state, action) => {
            state.isEdit = action.payload;
        },
        setEditingRecord: (state, action) => {
            state.editingRecord = action.payload;
        },
        setQuestionData: (state, action) => {
            state.data = action.payload;
        },
        setSelectedSubCategory: (state, action) => {
            state.selectedSubCategory = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchQuestions.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchQuestions.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = [...action.payload.data];
                state.total = action.payload.total;
            })
            .addCase(fetchQuestions.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch Question');
                state.error = action.payload || 'Failed to fetch Question';
            })
            // Create
            .addCase(createQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(createQuestion.fulfilled, (state, action: any) => {
                state.loading = false;
                state.total = state.total + 1;
                if (state.selectedSubCategory?.id === action?.payload?.subCategory)
                    state.data = [...state.data, action.payload];
            })
            .addCase(createQuestion.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create question';
            })
            // Update
            .addCase(updateQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateQuestion.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(updateQuestion.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update Question');
                state.error = action.payload || 'Failed to update question';
            })
            // Delete
            .addCase(deleteQuestion.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteQuestion.fulfilled, (state, action) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data = state.data.filter((item) => item.id !== action.payload.id);
                    state.total = state.total - 1;
                }
                state.loading = false;
            })
            .addCase(deleteQuestion.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete Question');
                state.error = action.payload || 'Failed to delete category';
            });
    },
});

export const { setModalOpen, setEdit, setEditingRecord, setQuestionData, setSelectedSubCategory } = questionSlice.actions;

export const selectQuestion = (state: RootState) => state.question;

export default questionSlice.reducer;
